import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { gql } from 'graphql-request'
import { useTheme } from '@/components/ThemeProvider'

import SplitLayout from '@/components/Onboarding/Layout/SplitLayout'
import { useAnalytics } from '@contexts/analytics'
import { useMutation, useQuery } from '@hooks/graphql'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { PhoneInput } from '@/components/ui/phoneinput'
import { countries } from '@/components/PhoneNumber'
import timezones from '@/helpers/timezones.json'
import MultiSelect from '@components/Multiselect'

const formSchema = z.object({
  firstName: z.string().nonempty({ message: 'First name is required' }),
  lastName: z.string().nonempty({ message: 'Last name is required' }),
  organizationRole: z.string().nonempty({ message: 'Role is required' }),
  subjects: z.array(z.object({
    value: z.string(),
    label: z.string()
  })).min(1, 'Select at least one subject'),
  locationCountryCode: z.string().nonempty({ message: 'Country is required' }),
  phone: z.object({
    phoneCountryCode: z.string().nonempty({ message: 'Country code is required' }),
    phoneNumber: z.string()
      .min(4, 'Phone number is too short')
      .max(16, 'Phone number is too long')
      .regex(/^[0-9]+$/, 'Phone number must be numeric')
  }),
  marketingSource: z.string().nonempty({ message: 'This question is required' })
})

const UPDATE_EDUCATOR_PROFILE_MUTATION = gql`
  mutation updateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      profile {
        firstName
        lastName
        organizationRole
        phoneCountryCode
        phoneNumber
        locationCountryCode
        subjects {
          id
          name
        }
      }
      errors {
        message
      }
    }
  }
`

const SUBJECTS_QUERY = gql`
  query subjects {
    subjects {
      id
      name
    }
  }
`

const EducatorDetails = ({
  nextStep,
  firstName,
  lastName,
  organizationRole,
  subjects,
  locationCountryCode,
  phoneCountryCode,
  phoneNumber,
  marketingSource
}) => {
  const { track } = useAnalytics()
  const { resolvedTheme } = useTheme()
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: firstName || '',
      lastName: lastName || '',
      organizationRole: organizationRole || '',
      subjects: subjects.map(s => ({ value: s.id, label: s.name })),
      locationCountryCode: locationCountryCode || '',
      phone: {
        phoneCountryCode: phoneCountryCode || '',
        phoneNumber: phoneNumber || ''
      },
      marketingSource: marketingSource || ''
    }
  })

  const { mutate: updateEducatorProfile, isLoading: isUpdating } = useMutation({
    gqlMutation: UPDATE_EDUCATOR_PROFILE_MUTATION,
    onSuccess: () => {
      track('Educator Onboarding Step Completed', { step: 'educator' })
      nextStep()
    }
  })

  const { data: { subjects: allSubjects = [] } = {} } = useQuery({
    queryKey: ['subjects'],
    gqlQuery: SUBJECTS_QUERY,
    staleTime: 86400000 // refresh once a day
  })

  const onSubmit = (data) => {
    const { subjects, phone, ...rest } = data
    const subjectIds = subjects.map(subject => subject.value)
    updateEducatorProfile({
      input: {
        ...rest,
        phoneCountryCode: phone.phoneCountryCode,
        phoneNumber: phone.phoneNumber,
        subjectIds
      }
    })
  }

  useEffect(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (!timeZone) return

    const countryCode = timezones[timeZone]

    if (countryCode) {
      const currentPhone = form.getValues('phone') || {}
      form.setValue('phone', { ...currentPhone, phoneCountryCode: countryCode })
      form.setValue('locationCountryCode', countryCode)
    }
  }, [])

  return (
    <SplitLayout>

      <h1 className='text-2xl font-extrabold tracking-tight mb-7'>
        Tell us a little bit about yourself
      </h1>

      <Form {...form}>
        <form className='flex flex-col space-y-6' onSubmit={form.handleSubmit(onSubmit)}>
          <div className='flex gap-3'>
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder='First name' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder='Last name' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='phone'
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <PhoneInput value={field.value} onChange={field.onChange} error={error || {}} />
                </FormControl>
                <If condition={error?.phoneCountryCode?.message}>
                  <p className='text-destructive text-sm'>{error.phoneCountryCode.message}</p>
                </If>
                <If condition={error?.phoneNumber?.message}>
                  <p className='text-destructive text-sm'>{error.phoneNumber.message}</p>
                </If>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='locationCountryCode'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className='w-full'>
                    <SelectTrigger>
                      <SelectValue placeholder='Select your country' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='organizationRole'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Role</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl className='w-full'>
                    <SelectTrigger>
                      <SelectValue placeholder='Select role' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='teacher'>Teacher</SelectItem>
                    <SelectItem value='lecturer'>Lecturer</SelectItem>
                    <SelectItem value='professor'>Professor</SelectItem>
                    <SelectItem value='school_leader'>School leader</SelectItem>
                    <SelectItem value='head_of_department'>Head of department</SelectItem>
                    <SelectItem value='dean'>Dean</SelectItem>
                    <SelectItem value='edtech_specialist'>EdTech specialist</SelectItem>
                    <SelectItem value='district_leader'>District leader</SelectItem>
                    <SelectItem value='parent'>Parent</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='subjects'
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>What subjects do you teach?</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={allSubjects.map(subject => ({
                      value: subject.id,
                      label: subject.name
                    }))}
                    selected={field.value || []}
                    onChange={field.onChange}
                    placeholder='Select subjects...'
                    error={!!error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='marketingSource'
            render={({ field }) => (
              <FormItem>
                <FormLabel>How did you come across Mindjoy?</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl className='w-full'>
                    <SelectTrigger>
                      <SelectValue placeholder='Select an answer' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='WORD_OF_MOUTH'>From a colleague who uses Mindjoy</SelectItem>
                    <SelectItem value='BLOG'>Read an article on the Mindjoy blog</SelectItem>
                    <SelectItem value='EMAIL'>Received an email from Mindjoy</SelectItem>
                    <SelectItem value='EDUCATIONAL_INSTITUTION'>My educational institution</SelectItem>
                    <SelectItem value='PODCAST'>From listening to a podcast</SelectItem>
                    <SelectItem value='WORKSHOP'>Attended a Mindjoy workshop</SelectItem>
                    <SelectItem value='TEAM'>Spoke to someone on the Mindjoy team</SelectItem>
                    <SelectItem value='GOOGLE'>Google search</SelectItem>
                    <SelectItem value='LINKEDIN'>LinkedIn</SelectItem>
                    <SelectItem value='TWITTER'>X (formerly Twitter)</SelectItem>
                    <SelectItem value='FACEBOOK'>Facebook</SelectItem>
                    <SelectItem value='INSTAGRAM'>Instagram</SelectItem>
                    <SelectItem value='TIKTOK'>TikTok</SelectItem>
                    <SelectItem value='YOUTUBE'>YouTube</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type='submit'
            className='w-fit mt-2'
            loading={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Continue'}
          </Button>
        </form>
      </Form>

      <SplitLayout.SidePanel>
        <div className='w-full h-full flex flex-col items-center justify-center'>
          <div className='max-w-128 w-full p-10'>
            <Choose>
              <When condition={resolvedTheme === 'dark'}>
                <img src='/onboarding/students-needing-support-dark@2x.png' className='w-auto' />
              </When>
              <Otherwise>
                <img src='/onboarding/students-needing-support@2x.png' className='w-auto' />
              </Otherwise>
            </Choose>
          </div>
          <p className='text-center text-foreground text-sm'>
            Instant insights, not just averages.<br />
            Know who needs help and why.
          </p>
        </div>
      </SplitLayout.SidePanel>
    </SplitLayout>
  )
}

export default EducatorDetails
